/**
 * A narrow, deliberately limited pre-parse check — SPEC.md §9's "we never
 * paraphrase Mermaid's parser" rules out guessing *why* Mermaid's own
 * message says what it says, but it doesn't rule out stating an objective,
 * unambiguous fact about the source itself, independent of what Mermaid
 * reported.
 *
 * Bracket-balance checking was considered and rejected: Mermaid's own node
 * shapes include asymmetric delimiters (`A>Label]`, the flag shape — opens
 * with `>`, closes with `]`, by design), so a generic "your brackets don't
 * match" check would misfire on valid syntax — exactly the "confidently
 * state wrong causes" failure §9 warns against.
 *
 * An unclosed double-quoted string has no such ambiguity anywhere in
 * Mermaid's grammar — every diagram type's strings are `"..."` on one line
 * — so an odd count of `"` on a line is never legitimate syntax. This is
 * the one check narrow enough to add without inventing a wrong cause.
 */

/** Strips a trailing `%%` line comment (SPEC.md §4) so its content never counts toward quote balance. */
function stripLineComment(line: string): string {
  const index = line.indexOf('%%');
  return index === -1 ? line : line.slice(0, index);
}

export interface SyntaxHint {
  line: number;
}

/**
 * Returns the 1-based line number of the first line with an odd number of
 * `"` characters (comment suffix excluded), or null when every line
 * balances. Mermaid strings never span multiple lines, so an odd count on
 * a single line is always an unclosed quote, never a false positive from a
 * string that continues onto the next line.
 */
export function detectUnclosedQuote(source: string): SyntaxHint | null {
  const lines = source.split('\n');
  for (let i = 0; i < lines.length; i++) {
    const content = stripLineComment(lines[i] ?? '');
    const quoteCount = (content.match(/"/g) ?? []).length;
    if (quoteCount % 2 !== 0) return { line: i + 1 };
  }
  return null;
}
